import {
  Paper,
  Avatar,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useLogin } from "../../Query/useAuth";
import colors from "../../Constants/colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { ComoInput } from "../Common/ComoInput";
import SplashScreen from "../Common/SplashScreen";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { mutate: loginMutate, isPending: isLoading } = useLogin();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const entraFailed = searchParams.get("entra-failed") === "true";

    if (entraFailed) {
      enqueueSnackbar("Entra ID login failed", { variant: "error" });
      setSearchParams({});
    }
  }, [enqueueSnackbar, searchParams, setSearchParams]);

  const handleChangeUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleLogin = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    loginMutate(
      { username, password },
      {
        onSuccess: async (response) => {
          if (response && response.cih_token) {
            localStorage.setItem("cih_token", response.cih_token);
            queryClient.removeQueries();
            navigate("/home");
          }
        },
      }
    );
  };

  const handleMicrosoftLogin = () => {
    window.location.href = `${process.env.REACT_APP_BASE_URL}/v1/auth/login-microsoft/`;
  };

  const handleNavigateToPasswordReset = () => {
    navigate("/password-reset-request");
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  if (isLoading) {
    return <SplashScreen />;
  }

  return (
    <Box
      sx={{
        height: "100vh",
        minHeight: "100vh",
        overflow: "hidden",
        backgroundColor: colors.gray50,
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: 3,
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${colors.gray200}`,
            borderLeft: `4px solid ${colors.primary_dark}`,
            backgroundColor: colors.white,
            maxWidth: { xs: "100%", sm: 480 },
            width: "100%",
            transform: "scale(0.9)",
            transformOrigin: "center center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                backgroundColor: colors.white,
                marginBottom: 2,
                border: `2px solid ${colors.primary_dark}`,
              }}
            >
              <img
                src="/commo_logo.png"
                alt="Logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                color: colors.primary_dark,
                fontWeight: 600,
                textAlign: "center",
                mb: 1,
              }}
            >
              Connected Innovation Hub
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.gray600, textAlign: "center" }}
            >
              Welcome back! Please sign in to continue.
            </Typography>
          </Box>

          <form onSubmit={handleLogin}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <ComoInput
                label="Email"
                required
                value={username}
                onChange={handleChangeUsername}
                disabled={isLoading}
                primaryColor={colors.primary_dark}
                multiline={false}
                maxRows={1}
                size="medium"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: colors.gray50,
                    "&.Mui-focused": {
                      backgroundColor: colors.white,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: colors.gray100,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: `${colors.primary_dark} !important`,
                    "&::placeholder": {
                      color: `${colors.gray400} !important`,
                      opacity: 1,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: `${colors.gray600} !important`,
                    "&.Mui-focused": {
                      color: `${colors.primary_dark} !important`,
                    },
                  },
                }}
              />

              <ComoInput
                label="Password"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={handleChangePassword}
                disabled={isLoading}
                primaryColor={colors.primary_dark}
                multiline={false}
                maxRows={1}
                size="medium"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        sx={{ color: colors.gray600 }}
                      >
                        {showPassword ? (
                          <Visibility sx={{ fontSize: 20 }} />
                        ) : (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: colors.gray50,
                    "&.Mui-focused": {
                      backgroundColor: colors.white,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: colors.gray100,
                    },
                  },
                  "& .MuiInputBase-input": {
                    color: `${colors.primary_dark} !important`,
                    "&::placeholder": {
                      color: `${colors.gray400} !important`,
                      opacity: 1,
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: `${colors.gray600} !important`,
                    "&.Mui-focused": {
                      color: `${colors.primary_dark} !important`,
                    },
                  },
                }}
              />

              <Typography
                component="button"
                type="button"
                onClick={handleNavigateToPasswordReset}
                sx={{
                  display: "block",
                  textAlign: "right",
                  color: colors.primary_dark,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  alignSelf: "flex-end",
                }}
              >
                Forgot Password?
              </Typography>

              <Button
                type="submit"
                variant="modern"
                fullWidth
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  mt: 1,
                }}
              >
                Sign In
              </Button>

              <Divider
                sx={{
                  width: "100%",
                  my: 2,
                  color: colors.gray500,
                  fontSize: "0.875rem",
                  "&::before, &::after": {
                    borderColor: colors.gray200,
                  },
                }}
              >
                Or sign in with
              </Divider>

              <Button
                onClick={handleMicrosoftLogin}
                variant="outlined"
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  borderColor: colors.gray200,
                  color: colors.primary_dark,
                  backgroundColor: colors.white,
                  "&:hover": {
                    borderColor: colors.primary_dark,
                    backgroundColor: colors.gray50,
                  },
                }}
                startIcon={
                  <img
                    src="microsoft_logo.png"
                    alt="Microsoft Logo"
                    style={{ width: 24, height: 24 }}
                  />
                }
              >
                Microsoft Entra ID
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Box
        sx={{
          position: "fixed",
          left: 20,
          bottom: 20,
          zIndex: 1100,
          display: { xs: "none", md: "block" },
        }}
      >
        <img
          src="/logo_big_blue.png"
          alt="Commo Logo"
          style={{ maxHeight: "50px" }}
        />
      </Box>
    </Box>
  );
};

export default Login;
